import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Map as MapIcon, Navigation } from "lucide-react";
import api from "../lib/api";
import { createMap, tealPin, L } from "../lib/leafletMap";

// Wave 25.17 — replaced Google Maps JS SDK with Leaflet + OSM tiles.
// Backend `/sites/{id}/resolve-coords` is unchanged; only the renderer
// switched. Total client-side cost for this surface: $0.

export default function MapView() {
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const [sites, setSites] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [s, c] = await Promise.all([api.get("/sites"), api.get("/customers")]);
        setSites(s.data);
        setCustomers(c.data);
      } catch (e) {
        setError("Could not load sites.");
        // eslint-disable-next-line no-console
        console.error(e);
      }
    })();
  }, []);

  useEffect(() => {
    if (!sites.length || !mapEl.current) return;
    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }
    const { map } = createMap(mapEl.current, { center: [-25.4753, 30.9694], zoom: 6 });
    mapRef.current = map;
    const markers = [];
    let cancelled = false;

    (async () => {
      for (const s of sites) {
        if (cancelled) return;
        let pos = null;
        if (s.lat && s.lng) pos = { lat: s.lat, lng: s.lng };
        else if (s.id && s.address) {
          try {
            const { data } = await api.post(`/sites/${s.id}/resolve-coords`);
            if (typeof data?.lat === "number" && typeof data?.lng === "number") {
              pos = { lat: data.lat, lng: data.lng };
            }
          } catch {
            continue;
          }
        }
        if (!pos) continue;
        const marker = L.marker([pos.lat, pos.lng], { icon: tealPin(), title: s.name }).addTo(map);
        marker.bindTooltip(s.name, { direction: "top", offset: [0, -28] });
        marker.on("click", () => setSelected({ ...s, ...pos }));
        markers.push(marker);
      }
      if (markers.length > 0) {
        const group = L.featureGroup(markers);
        map.fitBounds(group.getBounds().pad(0.2));
        if (markers.length === 1) map.setZoom(13);
      }
    })();

    return () => {
      cancelled = true;
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [sites]);

  const customerName = (id) => customers.find((c) => c.id === id)?.name || "—";

  return (
    <div className="space-y-5" data-testid="map-page">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-[#647087]">Field</div>
        <h1 className="font-display text-2xl md:text-4xl font-bold tracking-tight mt-1 flex items-center gap-3">
          <span className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
          <MapIcon className="w-7 h-7 text-blue-400" /> Site map
          <span className="text-sm text-blue-300/80 font-normal ml-1">{sites.length}</span>
        </h1>
      </div>

      <div className="grid lg:grid-cols-[1fr_320px] gap-4">
        <div className="hv-card overflow-hidden h-[600px] relative">
          {error && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/60 text-sm text-rose-300 p-4 text-center">
              {error}
            </div>
          )}
          <div ref={mapEl} className="w-full h-full" data-testid="map-canvas" />
        </div>
        <div className="hv-card p-4 max-h-[600px] overflow-y-auto">
          <h3 className="font-display text-xl font-bold mb-3">All sites ({sites.length})</h3>
          <div className="space-y-2">
            {sites.map((s) => {
              const q = s.lat && s.lng ? `${s.lat},${s.lng}` : encodeURIComponent(s.address);
              return (
                <div
                  key={s.id}
                  className={`border rounded-md p-2 cursor-pointer transition ${
                    selected?.id === s.id
                      ? "border-[#1fb6c1] bg-[#1fb6c1]/10"
                      : "border-white/5 hover:border-white/20"
                  }`}
                  onClick={() => setSelected(s)}
                  data-testid={`map-site-${s.id}`}
                >
                  <div className="text-sm font-medium">{s.name}</div>
                  <div className="text-xs text-[#647087]">{customerName(s.customer_id)}</div>
                  <div className="text-xs text-[#a0aabf] truncate mt-0.5">{s.address}</div>
                  <div className="flex gap-1 mt-2">
                    <Link
                      to={`/customers/${s.customer_id}`}
                      className="hv-btn hv-btn-ghost text-xs px-2 py-1 flex-1"
                    >
                      Customer
                    </Link>
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${q}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hv-btn hv-btn-primary text-xs px-2 py-1 flex-1"
                    >
                      <Navigation className="w-3 h-3" /> Go
                    </a>
                  </div>
                </div>
              );
            })}
            {sites.length === 0 && <div className="text-sm text-[#647087]">No sites yet.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
